import * as d3 from 'd3'
import { useRef } from 'react'
import { useD3, type D3Svg, type D3Dims } from './useD3.js'
import { CHART_PALETTE } from './chartTokens.js'
import { useChartTooltip, type TooltipVariant } from './useChartTooltip.js'
import { ChartLegend } from './ChartLegend.js'
import styles from './LineChart.module.css'

export interface DataPoint {
  date: string
  value: number
}

export interface Series {
  key: string
  label: string
  data: DataPoint[]
  color?: string
}

interface LineChartProps {
  series: Series[]
  areaFill?: boolean
  showTooltip?: boolean
  formatTooltip?: (point: DataPoint, series: Series) => string
  showLegend?: boolean
  onBrush?: (range: [Date, Date]) => void
  tooltipVariant?: TooltipVariant
  className?: string
}

interface TooltipCtx {
  show: (x: number, y: number, text: string) => void
  hide: () => void
  format?: (point: DataPoint, series: Series) => string
}

const MARGIN = { top: 16, right: 16, bottom: 32, left: 48 }

function renderChart(
  svg: D3Svg,
  dims: D3Dims,
  series: Series[],
  areaFill: boolean,
  tt?: TooltipCtx,
  onBrush?: (range: [Date, Date]) => void,
) {
  const w = dims.width - MARGIN.left - MARGIN.right
  const h = dims.height - MARGIN.top - MARGIN.bottom
  if (w <= 0 || h <= 0) return

  const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`)

  const allDates = series.flatMap((s) => s.data.map((d) => new Date(d.date)))
  const allValues = series.flatMap((s) => s.data.map((d) => d.value))

  const x = d3.scaleTime()
    .domain(d3.extent(allDates) as [Date, Date])
    .range([0, w])

  // 데이터가 전부 0이어도 축이 무너지지 않게 최소 1 보장 (정수 카운트 지표 가정)
  const maxVal = d3.max(allValues) ?? 0
  const yMax = Math.max(1, maxVal)
  const y = d3.scaleLinear()
    .domain([0, yMax])
    .range([h, 0])

  g.append('g')
    .attr('transform', `translate(0,${h})`)
    .call(d3.axisBottom(x).ticks(5).tickSize(0).tickPadding(8))
    .call((el) => el.select('.domain').attr('stroke', '#E8E4DF'))
    .selectAll('text')
    .attr('fill', '#9E9996')
    .attr('font-size', '11px')

  g.append('g')
    .call(d3.axisLeft(y).ticks(Math.min(yMax, 4)).tickFormat(d3.format('d')).tickSize(-w).tickPadding(8))
    .call((el) => el.select('.domain').remove())
    .call((el) => el.selectAll('.tick line').attr('stroke', '#E8E4DF').attr('stroke-dasharray', '2,2'))
    .selectAll('text')
    .attr('fill', '#9E9996')
    .attr('font-size', '11px')

  // 각 시리즈를 정렬하여 보관
  const sortedSeries = series.map((s) => ({
    ...s,
    sorted: [...s.data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
  }))

  sortedSeries.forEach((s, i) => {
    const color = s.color ?? CHART_PALETTE[i % CHART_PALETTE.length]

    const line = d3.line<DataPoint>()
      .x((d) => x(new Date(d.date)))
      .y((d) => y(d.value))
      .curve(d3.curveMonotoneX)

    if (areaFill) {
      const area = d3.area<DataPoint>()
        .x((d) => x(new Date(d.date)))
        .y0(h)
        .y1((d) => y(d.value))
        .curve(d3.curveMonotoneX)

      g.append('path')
        .datum(s.sorted)
        .attr('fill', color)
        .attr('fill-opacity', 0.1)
        .attr('d', area)
    }

    g.append('path')
      .datum(s.sorted)
      .attr('fill', 'none')
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .attr('d', line)
  })

  // 크로스헤어 툴팁
  if (tt) {
    // 수직 가이드라인
    const guideLine = g.append('line')
      .attr('y1', 0)
      .attr('y2', h)
      .attr('stroke', '#E8E4DF')
      .attr('stroke-dasharray', '4,3')
      .attr('stroke-width', 1)
      .style('opacity', 0)

    // 각 시리즈별 강조점
    const dots = sortedSeries.map((s, i) => {
      const color = s.color ?? CHART_PALETTE[i % CHART_PALETTE.length]
      return g.append('circle')
        .attr('r', 4)
        .attr('fill', color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .style('opacity', 0)
    })

    const bisector = d3.bisector<DataPoint, Date>((d) => new Date(d.date)).left

    // 투명 오버레이 rect (전체 차트 영역)
    g.append('rect')
      .attr('width', w)
      .attr('height', h)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event)
        const xDate = x.invert(mx)

        // 모든 시리즈의 값을 한 줄씩 모음
        const dateStr = xDate.toLocaleDateString()
        const lines: string[] = [dateStr]
        let tooltipY = 0
        let count = 0

        sortedSeries.forEach((s, i) => {
          const idx = bisector(s.sorted, xDate, 1)
          const d0 = s.sorted[idx - 1]
          const d1 = s.sorted[idx]
          if (!d0 && !d1) return

          let closest: DataPoint
          if (!d0) closest = d1
          else if (!d1) closest = d0
          else closest = xDate.getTime() - new Date(d0.date).getTime() > new Date(d1.date).getTime() - xDate.getTime() ? d1 : d0

          const cx = x(new Date(closest.date))
          const cy = y(closest.value)

          // 가이드라인 위치 갱신
          guideLine.attr('x1', cx).attr('x2', cx).style('opacity', 1)

          // 강조점 위치 갱신
          dots[i].attr('cx', cx).attr('cy', cy).style('opacity', 1)

          if (tt.format) {
            lines.push(tt.format(closest, s))
          } else {
            lines.push(`${s.label}: ${closest.value.toLocaleString()}`)
          }

          tooltipY += cy
          count++
        })

        if (count > 0) {
          tt.show(event.clientX + 14, event.clientY - 10, lines.join('\n'))
        }
      })
      .on('mouseout', () => {
        guideLine.style('opacity', 0)
        dots.forEach((dot) => dot.style('opacity', 0))
        tt.hide()
      })
  }

  // 브러시 오버레이
  if (onBrush) {
    const brush = d3.brushX()
      .extent([[0, 0], [w, h]])
      .on('end', (event) => {
        if (!event.selection) return
        const [x0, x1] = event.selection as [number, number]
        onBrush([x.invert(x0), x.invert(x1)])
        g.select<SVGGElement>('.brush').call(brush.move, null)
      })

    g.append('g')
      .attr('class', 'brush')
      .call(brush)
  }
}

export function LineChart({
  series,
  areaFill = false,
  showTooltip: enableTooltip = false,
  formatTooltip,
  showLegend = false,
  onBrush,
  tooltipVariant,
  className,
}: LineChartProps) {
  const { tooltipElement, show, hide } = useChartTooltip(tooltipVariant)
  const formatRef = useRef(formatTooltip)
  formatRef.current = formatTooltip
  const brushRef = useRef(onBrush)
  brushRef.current = onBrush

  const ref = useD3(
    (svg, dims) => {
      const tt = enableTooltip ? { show, hide, format: formatRef.current } : undefined
      renderChart(svg, dims, series, areaFill, tt, brushRef.current)
    },
    [series, areaFill, enableTooltip, !!onBrush],
  )

  const legendItems = showLegend
    ? series.map((s, i) => ({
        label: s.label,
        color: s.color ?? CHART_PALETTE[i % CHART_PALETTE.length],
      }))
    : undefined

  return (
    <div className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <svg ref={ref} className={styles.svg} />
      {enableTooltip && tooltipElement}
      {legendItems && <ChartLegend items={legendItems} />}
    </div>
  )
}
